/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import { computed, observable, action } from 'mobx';
import Progress from './progress';
import TranslationUtil from '../../utils/translation';
import appsStore from '../apps';
import { UPDATE_TYPE } from '../../utils/modelMapper/appModelMapper/appJsonConstants';
import { LEFT_NAV_KEYS } from '../../utils/enums';

// Details on properties captured in wiki page: https://wiki.corp.adobe.com/display/ccd/Data+Model+for+Apps+Tile+Store+in+CCD
export default class App {
    sapcode;

    codexVersion; // Version for which tile is being shown

    installedVersion; //  Installed version of tile being shown
    // This is deprecated now. Use 'installedVersionsForUpdate'

    installedVersionsForUpdate; // Installed versions of update being shown

    allInstalledVersions; // All installed versions of this SAPCode (includes non-prod versions)

    updateType; // Update type of this app, in case it is available

    isLaunchable; // Is this app launchable?
    // If app is not installed, it is not launchable.
    // If the app is installed, and trial has not started/expired, it not launchable.

    @computed get tileVersion() {
        // in tile, installed version is always shown (label/tooltip).
        // For FAU, new label is shown and for tooltip, codexVersion
        // tileVersion = null for web apps
        if (this.platform === 'web') {
            return null;
        }
        return this.installedVersion || this.codexVersion;
    }

    @computed get localizedTileVersion() {
        return (appsStore.isMultipleVersionsAvailable(this.sapcode))
            ? TranslationUtil.getMessage('ccx.appTile.version-prefix') + this.tileVersion : null;
    }

    @computed get olderInstalledAppModels() {
        return appsStore.apps
            .filter(app => (app.sapcode === this.sapcode && app.isOlderInstalledVersion));
    }

    platform;

    productId;

    languages = [];

    supportedOS;

    @observable iconURL;

    appFamilyName;

    latestVersion;

    isUninstallable = false;

    versionInSession = null;

    isEOLApp = false;

    isIncompatible = false;

    incompatibilityReason;

    errorDetails;

    appLineage;

    showCompatibility;

    discoverEnabled;

    /**
     * Callback to be executed when a button is clicked
     * @type {function}
     * @param actionId
     * @param app
     * @param data: { source, localizedUpdateDescription }
     * third param takes an object for remaining params
     * source: passed by AppActionBar to indicate its caller
     * which can be pdp/apptile/information dialog etc.
     */
    onAppAction;

    actionIds;

    entitlementInfo;

    firstInstallTriggerTimeStamp = '';

    @observable isPDPDataAvailable = false;

    @observable labelList;

    @observable actionList;

    @observable appSectionBasedFields = [];

    @observable isOlderInstalledVersion = false;

    sortOrder;

    mode;

    statusSortOrder = 100;

    @observable progress = null;

    @observable appSectionFilter = [];

    subSectionFilter = [];

    @observable subHeadingFilter = [];

    isHidden = false;

    productWhatsNewUrl = '';

    lastModifiedTimeStamp = '';

    isAppInError = false;

    errorAction;

    isSupportedForLongTerm = false;

    osPlatform; // Possible values : win32, win64, osx10, osx10-64, macarm64, winarm64

    @action setOlderInstalledVersion(isOlderInstalledVersion) {
        this.isOlderInstalledVersion = isOlderInstalledVersion;
    }

    @action setActionList(actionList) {
        this.actionList = actionList;
    }

    @action updateProgress(inProgress = false, isWaiting = false, percentage = 0,
        localizedMessageText = '', isCancellable = false, status = '', actionInProgress) {
        if (this.progress != null) {
            this.progress.setInProgress(inProgress);
            this.progress.setPercentage(percentage);
            this.progress.setIsWaiting(isWaiting);
            this.progress.setIsCancellable(isCancellable);
            this.progress.setLocalizedMessageText(localizedMessageText);
            this.progress.setStatus(status);
        } else {
            this.progress = new Progress({
                inProgress,
                isWaiting,
                percentage,
                localizedMessageText,
                isCancellable,
                status,
                actionInProgress,
            });
        }
    }

    @action setIconUrl(iconUrl) {
        this.iconURL = iconUrl;
    }

    @action setAppSectionBasedFields(appSectionBasedFields) {
        this.appSectionBasedFields = appSectionBasedFields;
    }

    @action setSubHeadingFilter(filterList) {
        this.subHeadingFilter = filterList;
    }

    @action setLabelList(labelList) {
        this.labelList = labelList;
    }

    @action setLanguages(languages) {
        this.languages = languages;
    }

    @action resetProgress() {
        this.progress = null;
    }

    @action setErrorDetails(errorDetails) {
        this.errorDetails = errorDetails;
    }

    @action setPDPAvailabilityStatus(availabilityStatus) {
        this.isPDPDataAvailable = availabilityStatus;
    }

    @action setVersionInSession(versionInSession) {
        this.versionInSession = versionInSession;
    }

    @action setStatusSortOrder(statusSortOrder) {
        this.statusSortOrder = statusSortOrder;
    }

    @action setDiscoverEnabled(status) {
        this.discoverEnabled = status;
    }

    @action setAppSectionFilter(appSectionFilters) {
        this.appSectionFilter = appSectionFilters;
    }

    setEntitlementInfo(entitlement) {
        this.entitlementInfo = entitlement;
    }

    updateAppSectionFilters(appSectionFilters) {
        this.appSectionFilters = appSectionFilters;
    }

    updateSubSectionFilters(subSectionFilters) {
        this.subSectionFilter = subSectionFilters;
    }

    updateSubHeadingFilters(subHeadingFilters) {
        this.subHeadingFilter = subHeadingFilters;
    }

    isFullAppUpdate() {
        return this.updateType === UPDATE_TYPE.FULL_APP_UPDATE;
    }

    isPatchUpdate() {
        return this.updateType === UPDATE_TYPE.PATCH_UPDATE;
    }

    isFullBuildUpdate() {
        return this.updateType === UPDATE_TYPE.FULL_BUILD_UPDATE;
    }

    isAppInstalled() {
        const sectionFilter = [...this.appSectionFilter, ...this.subHeadingFilter];
        return (sectionFilter.includes('installed') || sectionFilter.includes('available_updates'));
    }

    isUpdateAvailableForApp() {
        return this.isUpdateAvailable || this.isPatchUpdate() || this.isFullBuildUpdate();
    }

    getDefaultSectionBasedField() {
        const defaultSectionBasedFields = this.appSectionBasedFields
            .filter(appSectionBasedField => !appSectionBasedField.appSectionFilter
                .includes(LEFT_NAV_KEYS.UPDATES_KEY));
        return defaultSectionBasedFields.length ? defaultSectionBasedFields[0] : null;
    }

    getUpdateSectionBasedField() {
        const updateSectionBasedFields = this.appSectionBasedFields
            .filter(appSectionBasedField => appSectionBasedField.appSectionFilter
                .includes(LEFT_NAV_KEYS.UPDATES_KEY));
        return updateSectionBasedFields.length ? updateSectionBasedFields[0] : null;
    }

    updateActionHiddenStatus(inActionId, hidden) {
        this.appSectionBasedFields.forEach((sectionBasedField) => {
            sectionBasedField.setButtonList(sectionBasedField
                .buttonList.map((appAction) => {
                    if (appAction.actionId === inActionId) {
                        return {
                            ...appAction,
                            hidden,
                        };
                    }
                    return appAction;
                }));
            sectionBasedField.setDropDownList(sectionBasedField
                .dropDownList.map((appAction) => {
                    if (appAction.actionId === inActionId) {
                        return {
                            ...appAction,
                            hidden,
                        };
                    }
                    return appAction;
                }));
        });
    }

    doesMultipleVersionsExistInSection(sapcode, section, subSection, sectionHeader) {
        const appFilter = {
            appSectionFilter: section,
            sapcode,
        };
        if (subSection) {
            appFilter.subSectionFilter = subSection;
        }

        if (sectionHeader) {
            appFilter.subHeadingFilter = sectionHeader;
        }

        return appsStore.filterApps(appFilter).length > 1;
    }

    constructor({
        sapcode, codexVersion, platform, supportedOS,
        iconURL, appFamilyName, incompatibilityReason, errorDetails,
        appSectionBasedFields, progress, appSectionFilter, subSectionFilter,
        subHeadingFilter, onAppAction, actionIds, sortOrder, actionList, productId,
        labelList, otherVersionDetails, isEOLApp, isIncompatible, systemRequirementURL, appLineage,
	    showCompatibility, isUninstallable, isLaunchable,
        installedVersion, allInstalledVersions, entitlementInfo, isPDPDataAvailable,
        isHidden, mode, latestVersion, backUpIcon, discoverEnabled, productWhatsNewUrl,
        firstInstallTriggerTimeStamp, lastModifiedTimeStamp, isAppInError, errorAction,
        isUpdateAvailable, updateType, osPlatform, isSupportedForLongTerm,
    }) {
        this.sapcode = sapcode;
        this.codexVersion = codexVersion;
        this.platform = platform;
        this.iconURL = iconURL;
        this.backUpIcon = backUpIcon;
        this.appFamilyName = appFamilyName;
        this.isIncompatible = isIncompatible;
        this.incompatibilityReason = incompatibilityReason;
        this.systemRequirementURL = systemRequirementURL;
        this.errorDetails = errorDetails; // from Apps BL
        this.appSectionBasedFields = appSectionBasedFields;
        this.progress = progress;
        this.appSectionFilter = appSectionFilter;
        this.subSectionFilter = subSectionFilter;
        this.subHeadingFilter = subHeadingFilter;
        this.onAppAction = onAppAction;
        this.actionIds = actionIds;
        this.sortOrder = sortOrder; // this is the sortOrder per category and filter
        this.actionList = actionList;
        this.productId = productId;
        this.labelList = labelList;
        this.supportedOS = supportedOS;
        this.otherVersionDetails = otherVersionDetails;
        this.isEOLApp = isEOLApp;
        this.appLineage = appLineage;
        this.showCompatibility = showCompatibility;
        this.isUninstallable = isUninstallable;
        this.isLaunchable = isLaunchable;
        this.installedVersion = installedVersion;
        this.allInstalledVersions = allInstalledVersions;
        this.entitlementInfo = entitlementInfo;
        this.isPDPDataAvailable = isPDPDataAvailable || false;
        this.isHidden = isHidden || false;
        this.mode = mode;
        this.latestVersion = latestVersion;
        this.discoverEnabled = discoverEnabled;
        this.productWhatsNewUrl = productWhatsNewUrl;
        this.firstInstallTriggerTimeStamp = firstInstallTriggerTimeStamp;
        this.lastModifiedTimeStamp = lastModifiedTimeStamp;
        this.isAppInError = isAppInError;
        this.isUpdateAvailable = isUpdateAvailable;
        this.errorAction = errorAction;
        this.updateType = updateType;
        this.osPlatform = osPlatform;
        this.isSupportedForLongTerm = isSupportedForLongTerm;
    }
}
